/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

// Component imports
import { Modal, Space, Spin, Tooltip, Typography } from 'antd';
import AutomatedAnswerForm from '../Forms/AutomatedAnswerForm';
import { ExternalLinkIcon } from '../Icons/ExternalLinkIcon';

// Misc imports
import { ACTIVITY_TYPES, CHAPTERSOURCE_SCHEMAS } from '../../constants/main';
import { NAV_BOT } from '../../constants/navigation';
import { useLazyFetchChapterSourceQuery, useLazyFetchChapterSourcesQuery } from '../../store/chapterSources/chapterSourcesApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { truncate } from '../../utils/string/truncate';
import replaceUrlParams from '../../utils/string/replaceUrlParams';

/**
 * Given an entry point or activity ID, display it as a text that links to the corresponding activity/automated answer
 * @param {String} activityId The ID of the activity (either activityId or entryPointId must be provided)
 * @param {String} entryPointId The ID of the entry point (either activityId or entryPointId must be provided)
 * @param {Function} setSelectedAA The function to set the selected automated answer
 * @param {Boolean} showIcon Whether to show the external link icon
 * @param {Number} truncateCount The number of characters to truncate the activity name to
 * @param {String} notFoundText The text to display if the activity is not found
 * @returns {JSX.Element} The activity link
 */
const ActivityLink = ({
  activityId,
  botId = null,
  entryPointId,
  setSelectedAA,
  showIcon = true,
  truncateCount,
  notFoundText = i18n.t('Unknown flow'),
  fetchChaptersourcesIndividualy = true,
  openAAInNewTab = false
}) => {
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { currentBotLanguage, primaryBotLanguage } = useBotLanguage();

  const availableTenantId = tenantId || botId || null;
  const [matchingChaptersource, setMatchingChaptersource] = useState();
  const [isAutomatedAnswerModalOpen, setIsAutomatedAnswerModalOpen] = useState(false);

  const { data: existingChaptersources } = getCurrentQueryState('fetchChapterSources', { orgId, tenantId: availableTenantId, urlParams: { schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH } });

  // If the activityId is provided, fetch the chaptersource by ID
  // Else (entryPointId is provided), fetch all chaptersources and find the one that contains the entry point ID
  const [fetchChaptersources, { data: fetchedChaptersources, isFetching: isFetchingChaptersources }] = useLazyFetchChapterSourcesQuery();
  const [fetchChaptersource, { data: fetchedChaptersource, isFetching: isFetchingChaptersource }] = useLazyFetchChapterSourceQuery();

  const chaptersources = fetchedChaptersources || existingChaptersources;

  // Fetch whatever is needed on mount
  useEffect(() => {
    if (activityId && fetchChaptersourcesIndividualy) {
      fetchChaptersource({ orgId, tenantId: availableTenantId, chaptersourceId: activityId }, true);
    } else if (!chaptersources) {
      fetchChaptersources({ orgId, tenantId: availableTenantId, urlParams: { schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH } }, true);
    }
  }, [orgId, availableTenantId, activityId, fetchChaptersourcesIndividualy]);

  useEffect(() => {
    if (!chaptersources?.items) {
      return;
    }

    if (entryPointId) {
      setMatchingChaptersource(chaptersources.items.find(chaptersource => chaptersource.content.nodes.some(node => node.id === entryPointId)));
    } else if (activityId) {
      setMatchingChaptersource(chaptersources.items.find(chaptersource => chaptersource.id === activityId));
    }
  }, [activityId, entryPointId, chaptersources]);

  useEffect(() => {
    if (fetchedChaptersource) {
      setMatchingChaptersource(fetchedChaptersource);
    }
  }, [fetchedChaptersource]);

  if (isFetchingChaptersources || isFetchingChaptersource) {
    return (
      <Spin size="small" />
    );
  } else if (matchingChaptersource) {
    const shouldDisplayUUID = entryPointId ? matchingChaptersource.content.nodes.find(node => node.type === 'intro')?.id !== entryPointId : false;
    const flowName = matchingChaptersource.name[currentBotLanguage] || matchingChaptersource.name[primaryBotLanguage];
    const displayFlowName = shouldDisplayUUID ? `(${flowName})` : flowName;
    return (
      <Fragment>
        <Tooltip
          title={
            truncateCount && (matchingChaptersource.name[currentBotLanguage] || matchingChaptersource.name[primaryBotLanguage])?.length > truncateCount
            && <Space direction="vertical">
              <span>
                <Typography.Paragraph className="mb-0" strong type="secondary">{t('Flow Name')}:</Typography.Paragraph>
                <Typography.Paragraph type="secondary" className="mb-0">{shouldDisplayUUID ? `${entryPointId} ${displayFlowName}` : displayFlowName}</Typography.Paragraph>
              </span>
            </Space>
          }
          placement="right"
          mouseEnterDelay="0.5"
        >
          <Fragment>
            {shouldDisplayUUID ? <Typography.Text className="w-auto pb-0">{entryPointId}</Typography.Text> : null}
            <Typography.Link
              ellipsis={truncateCount > 0}
              className="p-0 w-auto word-break-break-all pointer-events-all"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();

                if (matchingChaptersource.type === ACTIVITY_TYPES.AUTOMATED_ANSWERS || matchingChaptersource.type === ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE) {
                  const urlSearchParams = new URLSearchParams(window.location.search);
                  if (window.location.pathname.includes('bots') && urlSearchParams.get('tab') === 'automated-answers') {
                    setSelectedAA(matchingChaptersource);
                  } else if (openAAInNewTab) {
                    window.open(`${replaceUrlParams(NAV_BOT, { orgId, tenantId: availableTenantId })}?view=behavior&tab=automated-answers&edit=${matchingChaptersource.id}`);
                  } else {
                    setIsAutomatedAnswerModalOpen(true);
                  }
                } else {
                  window.open(`${replaceUrlParams(NAV_BOT, { orgId, tenantId: availableTenantId })}?view=behavior&tab=flows&activity=${matchingChaptersource.id}`);
                }
              }}
            >
              <Space>
                {truncateCount
                  ? truncate(displayFlowName, truncateCount, '...')
                  : displayFlowName}
                {showIcon ? <ExternalLinkIcon /> : null}
              </Space>
            </Typography.Link>
          </Fragment>
        </Tooltip>

        {
          matchingChaptersource.type === ACTIVITY_TYPES.AUTOMATED_ANSWERS ? (
            <Modal
              title={t('Edit Automated Answer')}
              open={isAutomatedAnswerModalOpen}
              onOk={() => setIsAutomatedAnswerModalOpen(false)}
              onCancel={() => setIsAutomatedAnswerModalOpen(false)}
              footer={null}
              style={{ maxWidth: 1200 }}
              width={'75%'}
              centered
              destroyOnClose
              className="ant-modal-dim"
              bodyStyle={{ maxHeight: '85vh', overflowY: 'auto' }}
            >
              <AutomatedAnswerForm
                selectedAA={matchingChaptersource}
                setSelectedAA={setMatchingChaptersource}
                hideTitle
              />
            </Modal>
          ) : null
        }
      </Fragment>
    );
  } else if (!availableTenantId && (entryPointId || activityId)) {
    return (
      <Typography.Link
        className="p-0 w-auto word-break-break-all"
      >
        <Space>
          {entryPointId || activityId || notFoundText}
          {showIcon ? <ExternalLinkIcon /> : null}
        </Space>
      </Typography.Link>
    );
  } else {
    return notFoundText;
  }
};

ActivityLink.propTypes = {
  activityId: PropTypes.string,
  entryPointId: PropTypes.string,
  setSelectedAA: PropTypes.func,
  showIcon: PropTypes.bool,
  truncateCount: PropTypes.number,
  notFoundText: PropTypes.string,
  fetchChaptersourcesIndividualy: PropTypes.bool,
  openAAInNewTab: PropTypes.bool,
  botId: PropTypes.string
};

export default ActivityLink;
